import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

type UseScrollToHashOptions = {
  /** When false, skip scrolling (e.g. dashboard still showing skeleton). */
  enabled?: boolean;
};

/**
 * Scroll to `location.hash` after route/hash changes.
 * Retries briefly so SPA navigations work when the target mounts asynchronously.
 */
export function useScrollToHash({ enabled = true }: UseScrollToHashOptions = {}) {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (!enabled || !hash) return;

    const id = decodeURIComponent(hash.replace(/^#/, '')).trim();
    if (!id) return;

    let cancelled = false;
    let rafId = 0;
    let timeoutId = 0;
    let attempts = 0;
    const maxAttempts = 60;

    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        timeoutId = window.setTimeout(() => {
          rafId = window.requestAnimationFrame(tryScroll);
        }, 50);
      }
    };

    // Let the destination page paint before the first attempt.
    timeoutId = window.setTimeout(tryScroll, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [enabled, hash, pathname]);
}
